// hooks/useUserPreferences.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  userPreferencesApi,
  WeightsDto,
  CreatePreferenceDto,
  UserFeedPreference
} from '../../../apis/user-preferences';
import { useToast } from '@chakra-ui/react';
import Cookies from 'js-cookie';

const QUERY_KEYS = {
  defaultPresets: ['user-preferences', 'defaults'] as const,
  userPresets: ['user-preferences', 'user'] as const,
  activePreset: ['user-preferences', 'active'] as const
};

export const useUserPreferences = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const token = Cookies.get('token');

  // Get default presets
  const defaultPresets = useQuery({
    queryKey: QUERY_KEYS.defaultPresets,
    queryFn: userPreferencesApi.getDefaultPresets,
    enabled: !!token
  });

  // Get user's custom presets
  const userPresets = useQuery({
    queryKey: QUERY_KEYS.userPresets,
    queryFn: userPreferencesApi.getUserPresets,
    enabled: !!token
  });

  // Get active preset
  const activePreset = useQuery({
    queryKey: QUERY_KEYS.activePreset,
    queryFn: userPreferencesApi.getActivePreset,
    enabled: !!token
  });

  // Update active preset weights
  const updateActiveWeights = useMutation({
    mutationFn: userPreferencesApi.updateActiveWeights,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Weights updated',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update weights',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Update preset weights
  const updatePresetWeights = useMutation({
    mutationFn: ({ presetId, weights }: { presetId: string; weights: WeightsDto }) =>
      userPreferencesApi.updatePresetWeights(presetId, weights),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userPresets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Weights updated',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update weights',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Set active preset from default
  const setActiveFromDefault = useMutation({
    mutationFn: userPreferencesApi.setActiveFromDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Preset applied',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to apply preset',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Set active preset from user preset
  const setActiveFromUserPreset = useMutation({
    mutationFn: userPreferencesApi.setActiveFromUserPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Preset applied',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to apply preset',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Create new user preset
  const createUserPreset = useMutation({
    mutationFn: userPreferencesApi.createUserPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userPresets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Preset created',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create preset',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Create new user preset with components (respects manual weights)
  const createUserPresetWithComponents = useMutation({
    mutationFn: userPreferencesApi.createUserPresetWithComponents,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userPresets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Custom feed mode created',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create custom feed mode',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Save current as preset
  const saveCurrentAsPreset = useMutation({
    mutationFn: ({ name, description, instruction }: { name: string; description?: string; instruction?: string }) =>
      userPreferencesApi.saveCurrentAsPreset(name, description, instruction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userPresets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Preset saved',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to save preset',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Delete user preset
  const deleteUserPreset = useMutation({
    mutationFn: userPreferencesApi.deleteUserPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userPresets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Preset deleted',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to delete preset',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  // Update preset name
  const updatePresetName = useMutation({
    mutationFn: ({ presetId, name }: { presetId: string; name: string }) =>
      userPreferencesApi.updatePresetName(presetId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userPresets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activePreset });
      toast({
        title: 'Preset name updated',
        status: 'success',
        duration: 2000
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update preset name',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    }
  });

  return {
    // Queries
    defaultPresets,
    userPresets,
    activePreset,

    // Mutations
    updateActiveWeights,
    updatePresetWeights,
    setActiveFromDefault,
    setActiveFromUserPreset,
    createUserPreset,
    createUserPresetWithComponents,
    saveCurrentAsPreset,
    deleteUserPreset,
    updatePresetName,

    // Loading states
    isLoading: defaultPresets.isLoading || userPresets.isLoading || activePreset.isLoading,

    // All presets combined for easier use
    allPresets: [
      ...(defaultPresets.data || []),
      ...(userPresets.data || [])
    ] as UserFeedPreference[]
  };
};
